import React, { useState } from 'react';

import { useEscapeToClose } from '../../hooks/useEscapeToClose';
// P-004 claims 3/4/17: hierarchical scope taxonomy — 5 canonical top-level categories.
// Flat data_elements keys are preserved (back-compatible with the existing API).
const SCOPE_CATEGORIES = [
  { label: 'Clinical', items: [['prescriptions', 'Prescriptions / Supplements']] },
  { label: 'Biometric', items: [['bodyCharacteristics', 'Basic Body Characteristics'], ['deviceData', 'Integrated Device Data'], ['healthObjectives', 'Health Objectives']] },
  { label: 'Nutrition', items: [['foodPreferences', 'Food Preferences'], ['dietPlans', 'Diet Plans'], ['mealPlans', 'Meal Plans (Coming Soon)', true]] },
  { label: 'Fitness', items: [['exercisePlans', 'Exercise Plans'], ['weightTrainingPlans', 'Weight Training Plans']] },
  { label: 'Documents', items: [['medicalRecords', 'Medical Records / Files']] },
];


function SendReportModal({ onClose, providers, onSendReport }) {
  useEscapeToClose(onClose);   // Escape dismisses this modal
  const [selectedData, setSelectedData] = useState({
    bodyCharacteristics: false,
    healthObjectives: false,
    prescriptions: false,
    medicalRecords: false,
    foodPreferences: false,
    dietPlans: false,
    exercisePlans: false,
    weightTrainingPlans: false,
    mealPlans: false,
    deviceData: false
  });

  const [selectedProviders, setSelectedProviders] = useState([]);
  const [error, setError] = useState('');

  const handleDataCheckbox = (key) => {
    setSelectedData(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    setError('');
  };

  const handleProviderCheckbox = (providerId) => {
    setSelectedProviders(prev => {
      if (prev.includes(providerId)) {
        return prev.filter(id => id !== providerId);
      } else {
        return [...prev, providerId];
      }
    });
    setError('');
  };

  const handleSendReport = () => {
    // Validation: at least one data element selected
    const hasDataSelected = Object.values(selectedData).some(v => v);
    if (!hasDataSelected) {
      setError('Please select at least one data element to send');
      return;
    }

    // Validation: at least one provider selected
    if (selectedProviders.length === 0) {
      setError('Please select at least one provider to receive the report');
      return;
    }

    // Pass selections to parent
    onSendReport(selectedData, selectedProviders);
    onClose();
  };

  return (
   <div className="modal-overlay">
      <div className="modal-content modal-content-md">
        <div className="modal-header">
          <h2 className="m-0">Send Data to Provider(s)</h2>
          <button onClick={onClose} className="modal-close text-white">&times;</button>
        </div>

        <div className="modal-body">
        <p className="modal-description">
          First choose which of your data elements you would like to include and then you can select which Healthcare Providers to send a report. The report will be generated in PDF format and emailed to your selected providers; you will be CC'd on this email. This email will include a 1-time passcode allowing your providers to use a link included in the same email to add comments or recommendations for you based on this report. Before using this feature make sure you have accurate email addresses for all of your providers. After making your selections you can dismiss this window and the process will complete in the background or you can monitor progress on the status bar.
        </p>

        <hr className="divider-subtle" />

        {error && (
          <div className="alert-error-box mb-20">
            {error}
          </div>
        )}

        <div className="grid grid-1fr-1fr gap-30 mb-30">
          {/* Section A: Data to Send */}
          <div>
            <h3 className="section-header">
              A. Data to Send
            </h3>

            <div className="checkbox-column">
              {SCOPE_CATEGORIES.map((cat) => (
                <div key={cat.label} className="scope-category">
                  <h4 className="scope-category-header text-14 mt-10 mb-5">{cat.label}</h4>
                  {cat.items.map(([key, label, disabled]) => (
                    <label key={key} className={disabled ? "label-disabled" : "checkbox-row"}>
                      <input
                        type="checkbox"
                        checked={selectedData[key]}
                        onChange={() => handleDataCheckbox(key)}
                        disabled={!!disabled}
                        className="mr-3 cursor-pointer"
                      />
                      <span className="text-base">{label}</span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Section B: Providers to Receive */}
          <div>
            <h3 className="section-header">
              B. Providers to Receive
            </h3>

            {providers.length === 0 ? (
              <p className="text-gray-500 text-italic text-14">
                No providers available. Please add providers first.
              </p>
            ) : (
              <div className="checkbox-column">
                {providers.map(provider => (
                  <label
                    key={provider.id}
                    className="label-flex"
                  >
                    <input
                      type="checkbox"
                      checked={selectedProviders.includes(provider.id)}
                      onChange={() => handleProviderCheckbox(provider.id)}
                      className="mr-10 mt-2 cursor-pointer"
                    />
                    <div>
                      <div className="font-medium text-14">
                        {provider.provider_name}
                      </div>
                      <div className="text-12 text-gray-500">
                        {provider.provider_type}
                        {provider.provider_specialty && ` - ${provider.provider_specialty}`}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="modal-footer">
          <button
            onClick={onClose}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleSendReport}
            className="btn btn-primary"
          >
            Send Reports
          </button>
        </div>
        </div>
      </div>
    </div>
  );
}

export default SendReportModal;
